import axios from "axios";
import { HttpClient, RequestParams } from "../../utils/api/http-client";
import IBaseListingResponse from "../../utils/api/base-listing-response";
import { ACCESS_TOKEN, LANGUAGE_CODE } from "../../utils/helpers/constants";
import { ITripQuery } from "../../models/trip/query";
import {
  ITrip,
  IConfirmTripPaymentRequest,
  IConfirmTripPaymentResponse,
} from "../../models/trip/response";
import {
  ITripRequest,
  IAddOrderToTripRequest,
  IRemoveOrderFromTripRequest,
  IExportTripExcel,
} from "../../models/trip/request";

class TripService<SecurityDataType = unknown> {
  http: HttpClient<SecurityDataType>;

  constructor(http: HttpClient<SecurityDataType>) {
    this.http = http;
  }

  getAllTrips = (query: ITripQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<ITrip>>({
      path: `/trips`,
      method: "GET",
      query,
      ...params,
    });

  getTrip = (id: number, params?: RequestParams) =>
    this.http.request<ITrip>({
      path: `/trips/${id}`,
      method: "GET",
      ...params,
    });

  createTrip = (data: ITripRequest, params?: RequestParams) =>
    this.http.request<ITrip>({
      path: "/trips",
      method: "POST",
      body: data,
      ...params,
    });

  updateTrip = (id: number, data: ITripRequest, params?: RequestParams) =>
    this.http.request<ITrip>({
      path: `/trips/${id}`,
      method: "PUT",
      body: data,
      ...params,
    });

  addOrderToTrip = (
    id: number,
    data: IAddOrderToTripRequest,
    params?: RequestParams
  ) =>
    this.http.request<ITrip>({
      path: `/trips/${id}/orders`,
      method: "POST",
      body: data,
      ...params,
    });

  removeOrderFromTrip = (
    id: number,
    data: IRemoveOrderFromTripRequest,
    params?: RequestParams
  ) =>
    this.http.request<ITrip>({
      path: `/trips/${id}/orders/${data.orderId}`,
      method: "DELETE",
      body: data,
      ...params,
    });

  getUnassignedOrders = (query?: ITripQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<any>>({
      path: `/trips/unassigned-orders`,
      method: "GET",
      query,
      ...params,
    });

  getDriverActiveTrip = (driverId: number, params?: RequestParams) =>
    this.http.request<{ data: ITrip | null }>({
      path: `/trips/driver/${driverId}/active`,
      method: "GET",
      ...params,
    });

  getCurrentDriverTrip = (query?: any, params?: RequestParams) =>
    this.http.request<{ data: ITrip | null }>({
      path: `/current-driver-trip`,
      method: "GET",
      query,
      ...params,
    });

  getDriverPendingTrips = (query?: any, params?: RequestParams) =>
    this.http.request<{ data: ITrip[] }>({
      path: `/driver/trips/pending`,
      method: "GET",
      query,
      ...params,
    });

  getDriverTripHistory = (query: ITripQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<ITrip>>({
      path: `/driver/trips/history`,
      method: "GET",
      query,
      ...params,
    });

  exportExcel = (data: IExportTripExcel, query: ITripQuery) =>
    axios.post(`${process.env.REACT_APP_BASE_API_URL}/trips/export`, data, {
      headers: {
        authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN) ||
          sessionStorage.getItem(ACCESS_TOKEN)
          }`,
        "Content-Type": "application/json",
        "accept-language": `${localStorage.getItem(LANGUAGE_CODE) ??
          process.env.REACT_APP_DEFAULT_LANGUAGE
          }`,
      },
      params: query,
      responseType: "arraybuffer",
    });

  confirmPayment = (
    id: number,
    data: IConfirmTripPaymentRequest,
    params?: RequestParams
  ) =>
    this.http.request<IConfirmTripPaymentResponse>({
      path: `/trips/${id}/confirm-payment`,
      method: "POST",
      body: data,
      ...params,
    });
}

export default TripService;
